import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { SitesCreatedComponent } from '@features/admin/@shared/modals/sites-created/sites-created.component';
import { Permission } from '@services/auth/permission.enum';
import {
  CreateFillSiteCommand,
  CreateSourceSiteCommand,
  EditFillSiteCommand,
  EditSourceSiteCommand,
  IQualifiedPersonSearchViewModel,
  ISourceSiteAttachmentViewModel,
  SiteStatus,
  SourceSiteAttachmentViewModel
} from '@services/cleanfill-api/cleanfill-api.generated';
import { CountryStoreService } from '@services/cleanfill-core/country-service/country-store.service';
import { HttpFillSitesService } from '@services/cleanfill-core/http-fill-sites/http-fill-sites.service';
import { HttpSourceSitesService } from '@services/cleanfill-core/http-source-sites/http-source-sites.service';
import { StoreFillSitesService } from '@services/cleanfill-core/store-fill-sites/store-fill-sites.service';
import { StoreQpService } from '@services/cleanfill-core/store-qp/store-qp.service';
import { StoreSourceSitesService } from '@services/cleanfill-core/store-source-sites/store-source-sites.service';
import { SearchQuery } from '@view-models/search-query/search-query';
import { SnackbarService } from '@widgets/site-common/snackbar/snackbar.service';
import { NgxPermissionsService } from 'ngx-permissions';
import { Observable } from 'rxjs';
import { filter, take } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class CrudSitesService {
  constructor(
    private dialog: MatDialog,
    private router: Router,
    private qpStore: StoreQpService,
    private srcSiteStore: StoreSourceSitesService,
    private fillSiteStore: StoreFillSitesService,
    private countryStoreService: CountryStoreService,
    private permissionsService: NgxPermissionsService,
    private snackbar: SnackbarService,
    private httpSrcSite: HttpSourceSitesService,
    private httpFillSite: HttpFillSitesService
  ) {}

  get qualifiedPersons$(): Observable<IQualifiedPersonSearchViewModel[]> {
    return this.qpStore.qualifiedPersons$;
  }

  get newlyCreatedSourceSiteUniqueId$(): Observable<string> {
    return this.srcSiteStore.createdSourceSiteUniqueId$;
  }

  get newlyCreatedFillSiteUniqueId$(): Observable<string> {
    return this.fillSiteStore.createdFillSiteUniqueId$;
  }

  get provinces(): { uniqueId: string; code: string; name: string; countryId: number }[] {
    return this.countryStoreService.provinces;
  }

  loadQualifiedPersons(query: SearchQuery): void {
    return this.qpStore.loadQps(query);
  }

  completeSiteCreation(fillSites: string[], sourceSites: string[]) {
    const dialogRef = this.dialog.open<SitesCreatedComponent, { fillSites: string[]; sourceSites: string[] }>(SitesCreatedComponent, {
      data: { fillSites, sourceSites }
    });

    return dialogRef;
  }

  resetQpOptions(): void {
    const query = new SearchQuery().setPageIndex(1).setPageSize(5);
    this.loadQualifiedPersons(query);
  }

  async createSourceSite(payload: CreateSourceSiteCommand): Promise<string> {
    this.srcSiteStore.clearNewlyCreatedSourceSite();

    this.srcSiteStore.createSourceSite(payload, () => {
      const actionText = this.getStatusText(payload.status);
      if (payload.status === SiteStatus.New || !actionText) {
        return;
      }

      this.snackbar.success(`Source Site (${payload.name}) was ${actionText}`);
    });
    return (await this.newlyCreatedSourceSiteUniqueId$.pipe(filter(Boolean), take(1)).toPromise()) as Promise<string>;
  }

  async createFillSite(payload: CreateFillSiteCommand) {
    this.fillSiteStore.clearNewlyCreatedFillSite();

    this.fillSiteStore.createFillSite(payload, () => {
      const actionText = this.getStatusText(payload.status);
      if (payload.status === SiteStatus.New || !actionText) {
        return;
      }

      this.snackbar.success(`Receiving Site (${payload.name}) was ${actionText}`);
    });
    return (await this.newlyCreatedFillSiteUniqueId$.pipe(filter(Boolean), take(1)).toPromise()) as Promise<string>;
  }

  editSourceSite(payload: EditSourceSiteCommand, fn = () => {}) {
    return new Promise((resolve, reject) => {
      this.srcSiteStore.editSourceSite(payload, async () => {
        fn();
        resolve();
        if (payload.status === SiteStatus.New) {
          return;
        }
        return await this.snackbar.success(`Source Site (${payload.name}) was updated!`);
      });
    });
  }

  editFillSite(payload: EditFillSiteCommand) {
    return new Promise((resolve, reject) => {
      this.fillSiteStore.editFillSite(payload, async () => {
        resolve();
        if (payload.status === SiteStatus.New) {
          return;
        }
        return await this.snackbar.success(`Receiving Site (${payload.name}) was updated!`);
      });
    });
  }

  async uploadSourceSiteAttachments(siteUniqueId: string, files: File[]): Promise<SourceSiteAttachmentViewModel[]> {
    const data = new FormData();
    data.append('SourceSiteUniqueId', siteUniqueId);
    files.forEach(file => data.append('Files', file, file.name));

    await this.httpSrcSite
      .uploadAttachments(data)
      .pipe(take(1))
      .toPromise();

    const attachments = await this.httpSrcSite
      .getAttachments(siteUniqueId)
      .pipe(take(1))
      .toPromise();
    if (!attachments) {
      return [];
    }

    return attachments;
  }

  async uploadFillSiteAttachments(siteUniqueId: string, files: File[]) {
    const data = new FormData();
    data.append('FillSiteUniqueId', siteUniqueId);
    files.forEach(file => data.append('Files', file, file.name));

    await this.httpFillSite
      .uploadAttachments(data)
      .pipe(take(1))
      .toPromise();

    const attachments = await this.httpFillSite
      .getAttachments(siteUniqueId)
      .pipe(take(1))
      .toPromise();
    if (!attachments) {
      return [];
    }

    return attachments;
  }

  async deleteSourceSiteFile(file: ISourceSiteAttachmentViewModel): Promise<void> {
    await this.httpSrcSite
      .deleteAttachment(file.uniqueId)
      .pipe(take(1))
      .toPromise();
  }

  async deleteSourceSite(uniqueId: string): Promise<void> {
    await this.httpSrcSite
      .deleteSourceSite(uniqueId)
      .pipe(take(1))
      .toPromise();
  }

  async deleteFillSite(uniqueId: string) {
    await this.httpFillSite
      .deleteFillSite(uniqueId)
      .pipe(take(1))
      .toPromise();
  }

  async deleteFillSiteFile(file: ISourceSiteAttachmentViewModel) {}

  async hasPermission(permission: Permission): Promise<boolean> {
    return await this.permissionsService.hasPermission(permission);
  }

  private getStatusText(status: SiteStatus): string {
    switch (status) {
      case SiteStatus.Draft:
        return 'drafted';
      case SiteStatus.Active:
        return 'created';
      default:
        return;
    }
  }
}
